import { create } from 'zustand';

export type GeoComplyStatus = 'idle' | 'checking' | 'verified' | 'blocked' | 'verification-required';
export type GeoComplyCheckMode = 'passive' | 'step-up';

/**
 * Why a geolocation compliance check was denied, mirroring the mobile apps'
 * wire names. The `kalshi_*` verdict rules are configured on GeoComply's
 * side; the rest are rollups derived from the engine's error summary.
 */
export type GeoComplyDenyReason =
    | 'failed_with_precise_location'
    | 'not_tradeable_state'
    | 'approximate_location'
    | 'requires_precise_location'
    | 'unconfirmed_boundary'
    | 'out_of_boundary'
    | 'blocked_software'
    | 'unknown';

/**
 * Why the last check ended without a verdict, when the SDK told us. BrowserGuard
 * reports `no_coordinates` (error 611) for a strict-mode check the browser
 * couldn't supply coordinates for — location services off or site permission
 * denied — so the recovery UI can say how to fix it.
 */
export type GeoComplyInconclusiveReason = 'no_coordinates';

interface GeoComplyActions {
    sessionStarted: () => void;
    checkStarted: (mode: GeoComplyCheckMode) => void;
    verificationPassed: (validUntil: number, usRegionCode?: string | null) => void;
    verificationFailed: (denyReason?: GeoComplyDenyReason, usRegionCode?: string | null) => void;
    checkInconclusive: (reason?: GeoComplyInconclusiveReason) => void;
    sessionEnded: () => void;
    sdkAvailabilityChanged: (isAvailable: boolean) => void;
}

interface GeoComplyState {
    status: GeoComplyStatus;
    checkMode: GeoComplyCheckMode | null;
    validUntil: number | null;
    /** US region code from the check's location; null outside the US or when unknown. */
    usRegionCode: string | null;
    /** Why the last check was denied; only meaningful while status is 'blocked'. */
    denyReason: GeoComplyDenyReason | null;
    /** Why the last check was inconclusive; only meaningful while status is 'verification-required'. */
    inconclusiveReason: GeoComplyInconclusiveReason | null;
    /**
     * True when the BrowserGuard script failed to load or loaded without
     * exposing its SDK global (typically a content blocker), so no location
     * check can run until a reload. Tracks script availability rather than
     * session state, so session lifecycle actions leave it untouched.
     */
    sdkUnavailable: boolean;
    actions: GeoComplyActions;
}

export const useGeoComplyStore = create<GeoComplyState>()((set) => ({
    status: 'idle',
    checkMode: null,
    validUntil: null,
    usRegionCode: null,
    denyReason: null,
    inconclusiveReason: null,
    sdkUnavailable: false,
    actions: {
        sessionStarted: () =>
            set({
                status: 'checking',
                checkMode: 'passive',
                validUntil: null,
                usRegionCode: null,
                denyReason: null,
                inconclusiveReason: null,
            }),
        // A passive refresh of a still-current verification runs in the
        // background: the previous result stays trusted (GeoComply's
        // buffer_time grace) so trading is not interrupted mid-check.
        checkStarted: (checkMode) =>
            set((state) =>
                checkMode === 'passive' && isGeoComplyVerificationCurrent(state)
                    ? { checkMode }
                    : {
                          status: 'checking',
                          checkMode,
                          validUntil: null,
                          usRegionCode: null,
                          denyReason: null,
                          inconclusiveReason: null,
                      },
            ),
        verificationPassed: (validUntil, usRegionCode = null) =>
            set({
                status: 'verified',
                checkMode: null,
                validUntil,
                usRegionCode,
                denyReason: null,
                inconclusiveReason: null,
            }),
        verificationFailed: (denyReason = 'unknown', usRegionCode = null) =>
            set({
                status: 'blocked',
                checkMode: null,
                validUntil: null,
                usRegionCode,
                denyReason,
                inconclusiveReason: null,
            }),
        // An inconclusive re-check keeps a still-current result trusted
        // through its buffered expiry; only a lapsed or absent verification
        // fails closed. Step-up checks reset to 'checking' first, so their
        // failures always land in the fail-closed branch.
        checkInconclusive: (reason) =>
            set((state) =>
                isGeoComplyVerificationCurrent(state)
                    ? { checkMode: null }
                    : {
                          status: 'verification-required',
                          checkMode: null,
                          validUntil: null,
                          usRegionCode: null,
                          denyReason: null,
                          inconclusiveReason: reason ?? null,
                      },
            ),
        sessionEnded: () =>
            set({
                status: 'idle',
                checkMode: null,
                validUntil: null,
                usRegionCode: null,
                denyReason: null,
                inconclusiveReason: null,
            }),
        sdkAvailabilityChanged: (isAvailable) => set({ sdkUnavailable: !isAvailable }),
    },
}));

export function getGeoComplyMonotonicTime(): number {
    return performance.now();
}

export function isGeoComplyVerificationCurrent(
    state: Pick<GeoComplyState, 'status' | 'validUntil'>,
    now = getGeoComplyMonotonicTime(),
): boolean {
    return state.status === 'verified' && state.validUntil != null && state.validUntil > now;
}

export function shouldShowGeoComplyIntervention(
    state: Pick<GeoComplyState, 'status' | 'checkMode' | 'validUntil'>,
    now = getGeoComplyMonotonicTime(),
): boolean {
    if (state.status === 'blocked' || state.status === 'verification-required') return true;
    if (state.status === 'checking') return state.checkMode === 'step-up';
    if (state.status === 'verified') return !isGeoComplyVerificationCurrent(state, now);

    return false;
}
